import { z } from "zod";
import {
  paginatedSchema,
  paginationQuerySchema,
  type PaginationQueryInput,
} from "../common/pagination.js";
import { idSchema, isoDateTimeSchema, userSummarySchema } from "../common/primitives.js";
import { workspaceRoleSchema } from "../workspaces/roles.js";
import { projectParamsSchema } from "../projects/project.schemas.js";

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

export const teamSchema = z.object({
  id: idSchema,
  workspaceId: idSchema,
  projectId: idSchema,
  name: z.string(),
  description: z.string().nullable(),
  icon: z.string().nullable(),
  teamLead: userSummarySchema.nullable(),
  memberCount: z.number().int().nonnegative(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type Team = z.infer<typeof teamSchema>;

export const teamMemberSchema = z.object({
  userId: idSchema,
  workspaceRole: workspaceRoleSchema,
  joinedAt: isoDateTimeSchema,
  user: userSummarySchema,
});
export type TeamMember = z.infer<typeof teamMemberSchema>;

export const teamDetailSchema = teamSchema.extend({
  members: z.array(teamMemberSchema),
});
export type TeamDetail = z.infer<typeof teamDetailSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export const MAX_TEAM_MEMBERS_PER_REQUEST = 100;

const teamNameSchema = z
  .string()
  .trim()
  .min(1, "Name is required")
  .max(80, "Name must be at most 80 characters");
const teamDescriptionSchema = z
  .string()
  .trim()
  .max(1000, "Description must be at most 1000 characters");
const teamIconSchema = z.string().trim().min(1).max(32);

export const createTeamSchema = z.object({
  name: teamNameSchema,
  description: teamDescriptionSchema.optional(),
  icon: teamIconSchema.optional(),
  teamLeadId: idSchema.nullable().optional(),
  memberIds: z.array(idSchema).max(MAX_TEAM_MEMBERS_PER_REQUEST).optional(),
});
export type CreateTeamInput = z.infer<typeof createTeamSchema>;

export const updateTeamSchema = z
  .object({
    name: teamNameSchema.optional(),
    description: teamDescriptionSchema.nullable().optional(),
    icon: teamIconSchema.nullable().optional(),
    teamLeadId: idSchema.nullable().optional(),
  })
  .refine((value) => Object.values(value).some((v) => v !== undefined), {
    error: "Provide at least one field to update",
  });
export type UpdateTeamInput = z.infer<typeof updateTeamSchema>;

export const addTeamMemberSchema = z.object({ userId: idSchema });
export type AddTeamMemberInput = z.infer<typeof addTeamMemberSchema>;

export const listTeamsQuerySchema = paginationQuerySchema;
export type ListTeamsQueryInput = PaginationQueryInput;

export const teamParamsSchema = projectParamsSchema.extend({ teamId: idSchema });
export const teamMemberParamsSchema = teamParamsSchema.extend({ userId: idSchema });

// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------

export const teamsResponseSchema = paginatedSchema(teamSchema);
export type TeamsResponse = z.infer<typeof teamsResponseSchema>;

export const teamResponseSchema = z.object({ team: teamDetailSchema });
export type TeamResponse = z.infer<typeof teamResponseSchema>;
